import Grade from './Grade'
import { X as IconX } from 'lucide-react'
import { Course as CourseType } from '@/data/models'
import { useCalculatorStore } from '@/store/calculatorStore'

function CourseGrade({ course }: { course: CourseType }) {
  const changeGrade = useCalculatorStore((state) => state.changeGrade)
  const removeCourse = useCalculatorStore((state) => state.removeCourse)

  return (
    <div
      className="grid grid-cols-[1fr_40px] @min-[700px]:grid-cols-[1fr_212px_160px_48px] @min-[700px]:items-center @min-[700px]:gap-6 text-sm"
      data-testid={`course-grade-section-${course.id}`}
    >
      <h3 className="@max-[700px]:col-span-3 @max-[700px]:text-base @max-[700px]:mb-2">
        {course.name}
      </h3>
      <div className="grid grid-cols-6 gap-1 @max-[700px]:h-10">
        {[1, 2, 3, 4, 5, 6].map((number) => (
          <Grade
            key={number}
            number={number}
            isSelected={course.grade === number}
            onClick={() =>
              changeGrade(
                course.id,
                course.grade === number ? 0 : number,
                'grade'
              )
            }
            data-testid={`grade-${number}-course-${course.id}`}
          />
        ))}
      </div>
      <div className="@max-[700px]:contents"></div>
      <div className="flex items-center justify-end">
        <button
          onClick={() => removeCourse(course.id)}
          data-testid={`remove-course-${course.id}`}
          className="text-sm text-warm-gray-700 hover:text-black hover:bg-warm-gray-200 cursor-pointer h-8 w-8 rounded-full flex items-center justify-center"
        >
          <IconX className="size-4" />
        </button>
      </div>
    </div>
  )
}

function ExamGrade({
  course,
  examNumber,
}: {
  course: CourseType
  examNumber: 'exam' | 'exam2'
}) {
  const decreaseNumExams = useCalculatorStore((state) => state.decreaseNumExams)
  const changeGrade = useCalculatorStore((state) => state.changeGrade)

  return (
    <div
      className="grid grid-cols-[1fr_40px] @min-[700px]:grid-cols-[1fr_212px_160px_48px] @min-[700px]:items-center @min-[700px]:gap-6 text-sm mt-1"
      data-testid={`${examNumber}-grade-section-${course.id}`}
    >
      <h3 className="hidden @min-[700px]:block text-nowrap ml-4 text-warm-gray-700">
        + eksamenskarakter
      </h3>
      <div className="grid grid-cols-6 gap-1 @max-[700px]:h-10">
        {[1, 2, 3, 4, 5, 6].map((number) => (
          <Grade
            key={number}
            number={number}
            isSelected={course[examNumber] === number}
            onClick={() =>
              changeGrade(
                course.id,
                course[examNumber] === number ? 0 : number,
                examNumber
              )
            }
            data-testid={`grade-${number}-${examNumber}-${course.id}`}
          />
        ))}
      </div>
      <div className="@max-[700px]:contents"></div>
      <div className="flex items-center justify-end">
        <button
          onClick={() => {
            decreaseNumExams(course.id)
            changeGrade(course.id, 0, examNumber)
          }}
          data-testid={`remove-${examNumber}-${course.id}`}
          className="text-sm text-warm-gray-700 hover:text-black hover:bg-warm-gray-200 cursor-pointer h-8 w-8 rounded-full flex items-center justify-center"
        >
          <IconX className="size-4" />
        </button>
      </div>
    </div>
  )
}

function Course(course: CourseType) {
  const increaseNumExams = useCalculatorStore((state) => state.increaseNumExams)

  return (
    <div className="@container">
      <div className="@max-[700px]:pb-4 @max-[700px]:pt-2 py-3 @max-[700px]:px-5 border-b border-warm-gray-400 @min-[700px]:border-warm-gray-300 @max-[700px]:shadow-[0_4px_0_0_#F0EDEA] relative">
        <CourseGrade course={course} />
        {course.numExams > 0 && <ExamGrade course={course} examNumber="exam" />}
        {course.numExams > 1 && (
          <ExamGrade course={course} examNumber="exam2" />
        )}
        {course.numExams < 2 && (
          <button
            onClick={() => increaseNumExams(course.id)}
            data-testid={`add-exam-${course.id}`}
            className="text-sm text-warm-gray-700 @min-[700px]:text-black hover:text-purple-700 cursor-pointer absolute top-2.5 right-5 @min-[700px]:top-4.5 @min-[700px]:right-46"
          >
            Legg til<span className="@min-[700px]:hidden"> eksamen</span>
          </button>
        )}
      </div>
    </div>
  )
}

export default Course
